import { fromDateInputValue, toDateInputValue } from './dateTime';

export const recurrenceFrequencies = ['daily', 'weekly', 'monthly', 'yearly'];

export const recurrenceWeekdays = [
  { value: 'su', label: 'Sun' },
  { value: 'mo', label: 'Mon' },
  { value: 'tu', label: 'Tue' },
  { value: 'we', label: 'Wed' },
  { value: 'th', label: 'Thu' },
  { value: 'fr', label: 'Fri' },
  { value: 'sa', label: 'Sat' },
];

export function createRecurrenceForm(recurrence = null) {
  return {
    enabled: Boolean(recurrence),
    frequency: recurrence?.frequency || 'weekly',
    interval: String(recurrence?.interval || 1),
    daysOfWeek: Array.isArray(recurrence?.daysOfWeek) ? recurrence.daysOfWeek : [],
    dayOfMonth: recurrence?.dayOfMonth ? String(recurrence.dayOfMonth) : '',
    monthOfYear: recurrence?.monthOfYear ? String(recurrence.monthOfYear) : '',
    endsAt: toDateInputValue(recurrence?.endsAt),
    occurrenceCount: recurrence?.occurrenceCount ? String(recurrence.occurrenceCount) : '',
    timezone:
      recurrence?.timezone ||
      Intl.DateTimeFormat().resolvedOptions().timeZone ||
      null,
    completedCount: recurrence?.completedCount ?? 0,
    lastCompletedAt: recurrence?.lastCompletedAt || null,
  };
}

function parsePositiveInteger(value, fallback = null) {
  const parsed = Number.parseInt(String(value || '').trim(), 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}

function parseNullableBoundedInteger(value, min, max) {
  const parsed = parsePositiveInteger(value, null);
  return parsed != null && parsed >= min && parsed <= max ? parsed : null;
}

export function buildRecurrencePayload(form, { includeCompletionState = true } = {}) {
  if (!form?.enabled) {
    return null;
  }

  const frequency = recurrenceFrequencies.includes(form.frequency)
    ? form.frequency
    : 'weekly';
  const payload = {
    frequency,
    interval: parsePositiveInteger(form.interval, 1),
    daysOfWeek: frequency === 'weekly' ? form.daysOfWeek || [] : [],
    dayOfMonth:
      frequency === 'monthly' || frequency === 'yearly'
        ? parseNullableBoundedInteger(form.dayOfMonth, 1, 31)
        : null,
    monthOfYear:
      frequency === 'yearly' ? parseNullableBoundedInteger(form.monthOfYear, 1, 12) : null,
    endsAt: form.endsAt ? fromDateInputValue(form.endsAt, 'end') || null : null,
    occurrenceCount: parsePositiveInteger(form.occurrenceCount, null),
    timezone: form.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || null,
  };

  if (includeCompletionState) {
    payload.completedCount = Number.isFinite(Number(form.completedCount))
      ? Number(form.completedCount)
      : 0;
    payload.lastCompletedAt = form.lastCompletedAt || null;
  }

  return payload;
}

export function updateRecurrenceFormField(form, field, value) {
  if (field === 'enabled') {
    return {
      ...form,
      enabled: Boolean(value),
    };
  }

  if (field === 'daysOfWeek') {
    const selected = new Set(form.daysOfWeek || []);
    if (selected.has(value)) {
      selected.delete(value);
    } else {
      selected.add(value);
    }

    return {
      ...form,
      daysOfWeek: recurrenceWeekdays
        .map((day) => day.value)
        .filter((day) => selected.has(day)),
    };
  }

  return {
    ...form,
    [field]: value,
  };
}

function pluralize(value, singular, plural = `${singular}s`) {
  return value === 1 ? singular : plural;
}

function formatFrequencyLabel(frequency) {
  switch (frequency) {
    case 'daily':
      return 'day';
    case 'monthly':
      return 'month';
    case 'yearly':
      return 'year';
    default:
      return 'week';
  }
}

export function formatRecurrence(recurrence) {
  if (!recurrence?.frequency) {
    return '';
  }

  const interval = recurrence.interval || 1;
  const unit = formatFrequencyLabel(recurrence.frequency);
  const parts = [
    interval === 1
      ? `Every ${unit}`
      : `Every ${interval} ${pluralize(interval, unit)}`,
  ];

  if (recurrence.frequency === 'weekly' && recurrence.daysOfWeek?.length) {
    const dayLabels = recurrence.daysOfWeek
      .map((day) => recurrenceWeekdays.find((entry) => entry.value === day)?.label)
      .filter(Boolean);
    if (dayLabels.length) {
      parts.push(`on ${dayLabels.join(', ')}`);
    }
  }

  if ((recurrence.frequency === 'monthly' || recurrence.frequency === 'yearly') && recurrence.dayOfMonth) {
    parts.push(`on day ${recurrence.dayOfMonth}`);
  }

  if (recurrence.frequency === 'yearly' && recurrence.monthOfYear) {
    parts.push(`in month ${recurrence.monthOfYear}`);
  }

  if (recurrence.occurrenceCount) {
    parts.push(`for ${recurrence.occurrenceCount} ${pluralize(recurrence.occurrenceCount, 'occurrence')}`);
  } else if (recurrence.endsAt) {
    parts.push(`until ${toDateInputValue(recurrence.endsAt)}`);
  }

  return parts.join(' ');
}
